/**
 * TermTooltip — Erklärt Trading-Begriffe per Hover (Risk-On, MA200, ATR, CRV …).
 * Audit-Welle W4. Pure Tailwind, kein Floating-UI nötig.
 */

import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TermEntry {
  short: string;
  /** Backwards compat: kurze einzeilige Erklärung. */
  long: string;
  /** Audit Z1: 3-Block-Struktur (Bedeutung / Warum wichtig / Für deinen Trade) */
  meaning?: string;
  why?: string;
  for_trade?: string;
}

export const TERM_GLOSSARY: Record<string, TermEntry> = {
  'risk-on': {
    short: 'Risk-On',
    long: 'Marktumfeld unterstützt Long-Setups und Breakouts. Trendfolge hat Rückenwind.',
    meaning: 'Der Markt ist eher bereit, Risiko zu kaufen.',
    why: 'In solchen Phasen funktionieren Long-Trades und Breakouts oft besser.',
    for_trade: 'Wenn dein Setup gut ist, hat es Rückenwind vom Gesamtmarkt — trotzdem Entry, Stop und CRV sauber prüfen.',
  },
  'risk-off': {
    short: 'Risk-Off',
    long: 'Defensive Phase. Neue Longs brauchen stärkere Bestätigung oder kleinere Positionsgröße.',
    meaning: 'Der Markt ist vorsichtiger. Anleger gehen eher aus Risikoanlagen raus.',
    why: 'Breakouts scheitern in solchen Phasen häufiger.',
    for_trade: 'Neue Long-Trades sollten kleiner sein, defensiver geplant oder nach klarer Bestätigung gewählt werden.',
  },
  'ma200': {
    short: 'MA200',
    long: 'Eine Linie im Chart, die den Durchschnittskurs der letzten 200 Handelstage zeigt.',
    meaning: 'Der Durchschnittskurs der letzten 200 Handelstage — ungefähr ein Trading-Jahr. Eine glatte Linie unter dem Kurs.',
    why: 'Wenn der Kurs darüber liegt, ist die Aktie langfristig im Aufwärtstrend. Liegt er darunter, ist sie im Abwärtstrend.',
    for_trade: 'Käufe nur, wenn der Kurs über dieser Linie liegt UND die Linie selbst nach oben zeigt. Sonst lieber abwarten.',
  },
  'ma50': {
    short: 'MA50',
    long: '50-Tage gleitender Durchschnitt. Mittelfristiger Trendfilter, oft erster Pullback-Punkt in einem Stage-2-Trend.',
    meaning: 'Durchschnittskurs der letzten 50 Handelstage.',
    why: 'Der MA50 ist die typische Pullback-Zone in einem Aufwärtstrend — viele Käufer steigen dort wieder ein.',
    for_trade: 'Wenn der Kurs auf den MA50 zurückkommt und dort mit grüner Kerze dreht, ist das ein guter Wiedereinstieg im Trend.',
  },
  'atr': {
    short: 'ATR',
    long: 'Average True Range — misst typische tägliche Schwankung. Hilft bei Stop-Abstand (z. B. 2× ATR) und Positionsgröße.',
    meaning: 'Die Aktie schwankt im Durchschnitt um einen bestimmten Betrag pro Tag.',
    why: 'ATR hilft, den Stop nicht zu eng und nicht zu weit zu setzen.',
    for_trade: 'Hohe ATR = mehr Platz für Stop nötig, dafür kleinere Position. Niedrige ATR = engerer Stop möglich.',
  },
  'crv': {
    short: 'Chance-Risiko-Verhältnis (CRV)',
    long: 'Wie viel kannst du gewinnen, wenn du 1 Euro riskierst?',
    meaning: 'Ein Verhältnis. Beispiel: CRV 2,0 heißt — du riskierst 1 Euro, um 2 Euro zu gewinnen.',
    why: 'Selbst wenn du nur jeden zweiten Trade gewinnst, machst du mit CRV 2 noch Plus. Bei CRV unter 1 verlierst du langfristig fast immer.',
    for_trade: 'Faustregel: nur Trades mit Chance-Risiko über 1,5 nehmen. Unter 1,5 ist das Risiko nicht den möglichen Gewinn wert.',
  },
  'vcp': {
    short: 'VCP',
    long: 'Volatility Contraction Pattern (Minervini). Mehrere abnehmende Kontraktionen vor Breakout — Zeichen für aufgesaugte Liquidität.',
    meaning: 'Eine Aktie macht mehrere immer kleiner werdende Konsolidierungen, bevor sie ausbricht.',
    why: 'Mark Minervini hat damit Top-Performer früh erkannt. Die abnehmende Volatilität zeigt: Verkäufer geben auf, Käufer dominieren.',
    for_trade: 'VCP-Setups haben hohe Trefferquoten — aber nur kaufen, wenn der Ausbruch mit Volumen kommt.',
  },
  'stage-2': {
    short: 'Stage 2',
    long: 'Phase nach Weinstein, in der ein Asset seinen Aufwärtstrend etabliert hat: Kurs > MA30W (~MA200), MA30W steigt, höhere Hochs.',
    meaning: 'Eine Aktie ist in einem klaren Aufwärtstrend (nach Stan Weinstein).',
    why: 'In Stage 2 funktionieren Long-Trades am besten — der Trend ist bewiesen, nicht nur eine Vermutung.',
    for_trade: 'Long-Trades sind in Stage 2 deutlich sicherer als in Stage 1 (Bodenbildung) oder Stage 3 (Topbildung).',
  },
  'donchian-55': {
    short: 'Donchian-55',
    long: '55-Tage-Hoch/Tief. Klassischer Breakout-Trigger (Turtle-Trader-Regel) — Ausbruch über das 55-Tage-Hoch ist Trend-Bestätigung.',
    meaning: 'Das höchste Hoch (oder tiefste Tief) der letzten 55 Handelstage.',
    why: 'Die berühmten Turtle-Trader haben mit dieser Regel Millionen verdient. Ein Bruch nach oben ist ein klares Trend-Signal.',
    for_trade: 'Ausbruch über das 55-Tage-Hoch = Long-Setup. Stop unter das 20-Tage-Tief ziehen.',
  },
  'r-multiple': {
    short: 'R-Multiple',
    long: 'Trade-Ergebnis in Vielfachen des initialen Risikos. +1R = Gewinn = Stop-Abstand. −1R = Stop-Out.',
    meaning: 'Dein Trade-Ergebnis in Vielfachen vom ursprünglichen Risiko.',
    why: 'Macht Trades vergleichbar — egal ob Konto 1.000 € oder 100.000 € hat. Ein +2R-Trade ist immer doppelt so gut wie ein +1R-Trade.',
    for_trade: 'Auf Dauer brauchst du eine Trefferquote × R-Multiple > 1, damit deine Strategie profitabel ist.',
  },
  'profit-factor': {
    short: 'Profit Factor',
    long: 'Wieviel Euro Gewinn machst du pro Euro Verlust?',
    meaning: 'Alle Gewinne addiert, geteilt durch alle Verluste addiert. Beispiel: Profit Factor 2,0 = du verdienst 2 Euro pro 1 Euro Verlust.',
    why: 'Unter 1,0 verliert die Strategie auf Dauer. Über 1,5 ist sie brauchbar. Über 2,0 ist sie wirklich gut.',
    for_trade: 'Im Backtest: unter 1,5 → Strategie noch nicht reif. Erst weiter feintunen oder verwerfen.',
  },
  'expectancy': {
    short: 'Erwartungswert pro Trade',
    long: 'Wie viel verdienst du im Durchschnitt pro Trade — gemessen in R (= dein Risiko pro Trade).',
    meaning: 'Beispiel: Erwartungswert 0,4 R bedeutet, jeder Trade bringt im Schnitt das 0,4-fache deines Risikos. Bei 100 Euro Risiko also 40 Euro Gewinn pro Trade.',
    why: 'Sagt dir, ob deine Strategie auf Dauer Geld macht. Negativ = du verlierst, egal wie diszipliniert du bist.',
    for_trade: 'Über 0,3 R pro Trade = solide. Unter 0,0 = sofort stoppen und Strategie überdenken.',
  },
  'avg-r': {
    short: 'Avg R / Trade',
    long: 'Wie viel verdienst du im Durchschnitt pro Trade — gemessen in R.',
    meaning: 'Identisch mit Erwartungswert pro Trade. R ist eine Einheit, die zeigt wie viele Vielfache deines Risikos du verdienst.',
    why: 'Das wichtigste Maß für Strategie-Qualität. Über alle Trades zusammen, nicht nur Gewinner.',
    for_trade: 'Über +0,3 R = solide Strategie. Bei 100 Euro Risiko pro Trade also 30 Euro Plus pro Trade im Schnitt.',
  },
  'fractional': {
    short: 'Fractional Shares',
    long: 'Aktienbruchteile (z. B. 0,40 Stück). IBKR-EU erlaubt 0,1, IBKR-US bis 0,01.',
    meaning: 'Du kannst Bruchteile einer Aktie kaufen statt nur ganze Stücke.',
    why: 'Bei teuren Aktien wie Berkshire B (≈ 450 €) oder ASML (≈ 800 €) wären sonst kleine Konten chancenlos.',
    for_trade: 'Mit Fractional Shares kannst du auch mit 5.000 € Konto eine ASML-Position aufbauen, ohne dass eine einzige Aktie schon 16 % Risiko ausmacht.',
  },
  'confidence': {
    short: 'Datenvertrauen',
    long: 'Wie viele wichtige Daten haben wir wirklich für diese Bewertung?',
    meaning: 'Wie viele wichtige Daten haben wir wirklich für diese Bewertung?',
    why: 'Wenn Daten fehlen, ist die Bewertung weniger sicher — auch wenn der Score hoch aussieht.',
    for_trade: 'Hoch = belastbar. Mittel = mit Vorsicht. Niedrig = lieber abwarten, bis die Daten da sind.',
  },
  'drawdown': {
    short: 'Drawdown',
    long: 'Wie weit die Aktie oder Strategie vom letzten Hoch gefallen ist.',
    meaning: 'Der Verlust vom letzten Hoch — wie tief ein Trade oder eine Strategie zwischendurch im Minus war.',
    why: 'Ein hoher Drawdown sagt: Der Trade kann stark gegen dich laufen, bevor er sich erholt. Du musst das durchhalten können.',
    for_trade: 'Strategien mit Max-DD > 35 % sind in der Praxis schwer durchzuhalten. Faustregel: lieber kleinere Position bei hohem DD-Risiko.',
  },
  'volumen': {
    short: 'Volumen',
    long: 'Wie viele Aktien gehandelt wurden.',
    meaning: 'Die Anzahl gehandelter Aktien an einem Tag oder in einer Woche.',
    why: 'Ein Ausbruch mit hohem Volumen ist überzeugender — viele Marktteilnehmer tragen ihn mit. Niedriges Volumen = wenig Überzeugung.',
    for_trade: 'Breakout ohne Volumen ist verdächtig — solche Ausbrüche scheitern oft. Lieber auf den nächsten mit Volumen warten.',
  },
  'volume-ratio': {
    short: 'Volumen-Ratio',
    long: 'Heutiges Volumen relativ zum 20-Tages-Schnitt.',
    meaning: 'Heutiges Volumen geteilt durch das durchschnittliche Volumen der letzten 20 Tage.',
    why: '> 1.5 = klarer Volume-Spike, oft Bestätigung für Breakouts. < 0.7 = unterdurchschnittliches Interesse, Setup ist schwächer.',
    for_trade: 'Beim Breakout-Trade lieber auf 1.5× Volume-Spike warten — das ist die institutionelle Bestätigung.',
  },
  'earnings': {
    short: 'Earnings',
    long: 'Quartalszahlen-Termin. Der Kurs kann stark springen, auch gegen deine Richtung.',
    meaning: 'Das Unternehmen veröffentlicht Quartalszahlen.',
    why: 'Der Kurs kann stark springen, auch gegen deine Richtung — Stop wird durch Kurssprung wirkungslos.',
    for_trade: 'Kurz vor Earnings ist ein neuer Trade riskanter. Innerhalb der Holding-Periode = Volatilitätsrisiko einplanen.',
  },
  'rs': {
    short: 'Relative Stärke',
    long: 'Die Aktie läuft besser als der Gesamtmarkt oder ein Vergleichsindex.',
    meaning: 'Die Aktie ist stärker als der Markt oder ihr Sektor — sie steigt mehr und fällt weniger.',
    why: 'Starke Aktien werden auch in guten Marktphasen am stärksten weiterlaufen. Schwache Aktien hinken hinterher, auch wenn der Markt steigt.',
    for_trade: 'Hohe Relative Stärke ist ein klarer Pluspunkt für Long-Trades. Aber: nicht allein als Einstiegssignal nehmen — Trend und Setup müssen passen.',
  },
  'liquidity': {
    short: 'Liquidität',
    long: 'Durchschnittliches Dollar-Volumen pro Tag.',
    meaning: 'Wie viel Geld pro Tag in der Aktie umgesetzt wird (Dollar-Volumen, nicht nur Stück-Volumen).',
    why: 'Hohe Liquidität = enge Spreads, kleine Slippage. Niedrige Liquidität = du bekommst beim Kauf einen schlechten Preis und beim Verkauf auch.',
    for_trade: 'Faustregel: Avg-Dollar-Volumen > 5 Mio. USD für saubere Swing-Trades. Drunter wird es teuer beim Ein- und Aussteigen.',
  },
  'pivot': {
    short: 'Pivot',
    long: 'Letzter signifikanter Hoch- oder Tiefpunkt im Chart.',
    meaning: 'Ein markanter Wendepunkt — letztes Hoch (Pivot-High) oder letztes Tief (Pivot-Low).',
    why: 'Pivots sind oft die Marken, an denen sich entscheidet, ob ein Trend weiterläuft (Breakout über Pivot-High) oder dreht.',
    for_trade: 'Klassischer Trigger: Long bei Bruch über das letzte Pivot-High, Stop unter das vorletzte Pivot-Low.',
  },
  // V6.2.A — Fundamentals-Begriffe (für Klartext-Erklärungen)
  'roic': {
    short: 'ROIC',
    long: 'Return on Invested Capital — wie effizient ein Unternehmen sein eingesetztes Kapital nutzt.',
    meaning: 'Wieviel Gewinn macht das Unternehmen pro investiertem Euro Kapital?',
    why: 'ROIC > 15 % über mehrere Jahre = sehr profitables Geschäft mit Wettbewerbsvorteil. Unter 10 % = mäßig oder zyklisch.',
    for_trade: 'Hohe und stabile ROIC sind ein Zeichen für Burggraben-Aktien. Diese halten Krisen besser durch.',
  },
  'roe': {
    short: 'ROE',
    long: 'Return on Equity — Eigenkapitalrendite.',
    meaning: 'Wieviel Gewinn macht das Unternehmen pro Euro Eigenkapital der Aktionäre?',
    why: 'ROE > 15 % über mehrere Jahre = profitables Geschäft. Sehr hohes ROE bei hohem Schuldenstand kann aber täuschen.',
    for_trade: 'ROE im Kontext zur Verschuldung ansehen — hohe ROE bei niedriger Verschuldung ist Qualitätsmerkmal.',
  },
  'roa': {
    short: 'ROA',
    long: 'Return on Assets — Gesamtkapitalrendite.',
    meaning: 'Wieviel Gewinn macht das Unternehmen pro Euro Vermögen (egal ob aus Eigenkapital oder Schulden)?',
    why: 'ROA zeigt die Effizienz des Geschäfts unabhängig davon, wie es finanziert ist. > 8 % ist solide für Industrie, > 15 % für Software.',
    for_trade: 'Bei zwei ähnlichen Aktien gewinnt die mit höherem ROA — das Geschäft ist einfach produktiver.',
  },
  'fcf': {
    short: 'Free Cashflow',
    long: 'Cashflow nach Investitionen — was wirklich für Aktionäre verfügbar ist.',
    meaning: 'Das Geld, das nach laufenden Kosten und Investitionen wirklich übrig bleibt.',
    why: 'Free Cashflow zeigt, ob ein Unternehmen wirklich Geld verdient — Gewinn allein kann durch Buchhaltungstricks verzerrt sein.',
    for_trade: 'Positiver, wachsender Free Cashflow ist Pflicht für Qualitätsaktien. Negativer FCF über mehrere Jahre = Warnsignal.',
  },
  'fcf-marge': {
    short: 'FCF-Marge',
    long: 'Free Cashflow geteilt durch Umsatz.',
    meaning: 'Wieviel Prozent vom Umsatz bleiben als echter Cashflow übrig?',
    why: 'FCF-Marge > 15 % ist sehr gut, > 25 % bei Software/Plattformen üblich. Niedrig = kapitalintensives Geschäft.',
    for_trade: 'Hohe FCF-Marge zeigt: Das Unternehmen hat Pricing-Power und braucht wenig Reinvestitionen.',
  },
  'operating-margin': {
    short: 'Operative Marge',
    long: 'Operativer Gewinn (vor Zinsen und Steuern) geteilt durch Umsatz.',
    meaning: 'Wieviel Prozent vom Umsatz bleiben als operativer Gewinn übrig?',
    why: 'Zeigt die Profitabilität des Kerngeschäfts. > 20 % = stark, > 30 % = exzellent (typisch Software).',
    for_trade: 'Steigende operative Marge über Jahre = das Geschäft wird besser. Sinkende = Warnsignal.',
  },
  'net-margin': {
    short: 'Nettomarge',
    long: 'Nettogewinn geteilt durch Umsatz.',
    meaning: 'Wieviel Prozent vom Umsatz bleiben als Nettogewinn nach allem (Steuern, Zinsen, Abschreibungen)?',
    why: 'Direkter Vergleich: Wer macht aus 100 € Umsatz mehr Gewinn? > 15 % ist solide, > 25 % exzellent.',
    for_trade: 'Im Sektorvergleich anschauen — Software hat hohe Margen, Einzelhandel niedrige. Vergleiche immer Äpfel mit Äpfeln.',
  },
  'debt-to-equity': {
    short: 'Verschuldungsgrad',
    long: 'Verhältnis von Schulden zu Eigenkapital.',
    meaning: 'Wie viel Schulden hat das Unternehmen pro Euro Eigenkapital?',
    why: '< 1.0 = solide. > 2.0 = riskant in Krisen, weil Zinslast erdrückt. Verschuldung ist nicht per se schlecht — aber sie macht verwundbar.',
    for_trade: 'Bei steigenden Zinsen leiden hochverschuldete Aktien zuerst. Defensiv: Verschuldungsgrad < 1.5 bevorzugen.',
  },
  'payout-ratio': {
    short: 'Ausschüttungsquote',
    long: 'Dividende geteilt durch Gewinn pro Aktie.',
    meaning: 'Wieviel Prozent vom Gewinn schüttet das Unternehmen als Dividende aus?',
    why: '< 50 % = nachhaltig, Spielraum für Erhöhungen. > 80 % = riskant, eine schlechte Quartalszahl könnte Kürzung erzwingen.',
    for_trade: 'Aristokraten haben oft Payout-Ratio um 40–60 % — sicher und mit Wachstumsspielraum. > 80 % ist Warnsignal für Kürzungsrisiko.',
  },
  'dividend-yield': {
    short: 'Dividendenrendite',
    long: 'Aktuelle Dividende relativ zum Kurs.',
    meaning: 'Wieviel Prozent vom Aktienkurs bekommst du jährlich als Dividende?',
    why: 'Wichtig für Income-Investoren. Aber: Sehr hohe Renditen (> 7 %) sind oft ein Warnsignal — der Kurs ist gefallen, weil Probleme da sind.',
    for_trade: 'Solide Dividendenaktien liegen meist bei 2–5 %. Höher = entweder echtes Schnäppchen oder gefährlich. Immer Payout-Ratio dazu prüfen.',
  },
  'sentiment': {
    short: 'Sentiment',
    long: 'Allgemeine Stimmung am Markt oder zu einer Aktie.',
    meaning: 'Wie ist die Stimmung der Marktteilnehmer? Positiv (bullisch), neutral oder negativ (bärisch)?',
    why: 'Sentiment kann selbsterfüllend werden — viele Käufer treiben den Kurs, viele Verkäufer drücken ihn. Aber: extremes Sentiment ist oft ein Kontraindikator.',
    for_trade: 'Negative Nachrichten + fallender Kurs auf wichtige Stützen = Beobachten. Wenn das Sentiment dreht, kann es schnell gehen.',
  },
  'sharpe': {
    short: 'Sharpe-Ratio',
    long: 'Rendite pro Einheit Risiko.',
    meaning: 'Wie viel Rendite bekommst du pro Einheit Risiko (Schwankung)?',
    why: 'Sharpe > 1.0 = solide, > 2.0 = sehr gut. Berücksichtigt nicht nur den Gewinn, sondern wie holprig der Weg dorthin war.',
    for_trade: 'Backtest mit Sharpe < 0.5 ist nicht produktionsreif. Eine Strategie mit niedrigem Sharpe macht zwar Gewinn, aber mit großen Drawdowns.',
  },
  'volatility': {
    short: 'Volatilität',
    long: 'Wie stark ein Kurs schwankt.',
    meaning: 'Die Schwankungsbreite eines Kurses — wie heftig er sich bewegt.',
    why: 'Hohe Volatilität = größere Stops nötig, kleinere Position. Niedrige Volatilität = engere Stops, größere Position möglich.',
    for_trade: 'Statt "kein Stop" oder "fixer Prozent-Stop" lieber Stop = 2× ATR (Average True Range) — das passt sich der Volatilität an.',
  },
  'sector-rotation': {
    short: 'Sektor-Rotation',
    long: 'Wechsel der Markt-Anführer zwischen Sektoren.',
    meaning: 'In welchen Sektoren fließt gerade Geld rein, aus welchen raus?',
    why: 'Im Frühaufschwung führen oft Tech und Industrie. In der Spätphase Energie und Rohstoffe. In Korrekturen defensive Sektoren wie Healthcare und Consumer Staples.',
    for_trade: 'Aktien aus aktuell starken Sektoren haben Rückenwind. Schwache Sektoren = harter Gegenwind, auch starke Aktien straucheln.',
  },
  // V7.W4 — neue Konzepte verständlich
  'datenvertrauen': {
    short: 'Datenvertrauen',
    long: 'Wie viele wichtige Daten haben wir wirklich für diese Bewertung?',
    meaning: 'Wie viele wichtige Daten haben wir wirklich für diese Bewertung?',
    why: 'Wenn Daten fehlen, ist die Bewertung weniger sicher — auch wenn der Score hoch aussieht.',
    for_trade: 'Hoch = belastbar. Mittel = mit Vorsicht. Niedrig = lieber abwarten, bis die Daten da sind.',
  },
  'plausibilitaet': {
    short: 'Plausibilität',
    long: 'Auto-Check, ob die Daten realistisch aussehen.',
    meaning: 'Auto-Check, ob die Daten realistisch aussehen.',
    why: 'Manchmal liefern Datenanbieter falsche Werte — zum Beispiel ein 52-Wochen-Hoch unter dem aktuellen Kurs.',
    for_trade: 'Wenn ein Wert klar falsch ist, wird er nicht in den Score gerechnet. Bei "verdächtig" lieber selbst prüfen.',
  },
  'edge': {
    short: 'Edge',
    long: 'Der durchschnittliche Gewinn deiner Strategie pro Trade — gemessen in R (= dein Risiko pro Trade).',
    meaning: 'Der durchschnittliche Gewinn deiner Strategie pro Trade — gemessen in R (= dein Risiko pro Trade).',
    why: 'Ohne Edge verlierst du auf Dauer Geld, egal wie diszipliniert du bist.',
    for_trade: 'Über 0,4 R = stark. 0,2 bis 0,4 R = brauchbar. Unter 0,2 R = nicht traden, Strategie überarbeiten.',
  },
  // V8.TEXT.2 — neue Klartext-Einträge für Score, Risiko, Briefing, Watchlist-Bewertung, fehlende Daten.
  'score': {
    short: 'Score',
    long: 'Eine Note von 0 bis 100, wie gut die Aktie aktuell zum Setup passt.',
    meaning: 'Eine Note von 0 bis 100, wie gut die Aktie aktuell zum Setup passt.',
    why: 'Hoher Score = mehr Pluspunkte, weniger Risiken. Niedrig = Setup ist schwach oder Daten fehlen.',
    for_trade: 'Ab 70 ist es ernst zu nehmen. Unter 50 lieber abwarten.',
  },
  'risiko': {
    short: 'Offenes Risiko',
    long: 'Wieviel Geld verlierst du im schlimmsten Fall, wenn alle deine offenen Positionen und Pläne ihren Stop treffen?',
    meaning: 'Wieviel Geld verlierst du im schlimmsten Fall, wenn alle deine offenen Positionen und Pläne ihren Stop treffen?',
    why: 'Mehrere Positionen können sich addieren. Wenn alle gleichzeitig schief gehen, willst du nicht zu viel auf einmal verlieren.',
    for_trade: 'Faustregel: nicht mehr als 5 bis 10 Prozent vom Konto gleichzeitig im Risiko. Über 10 Prozent wird es eng.',
  },
  'fehlende-daten': {
    short: 'Fehlende Daten',
    long: 'Wichtige Daten zu dieser Aktie sind aktuell nicht verfügbar.',
    meaning: 'Wichtige Daten zu dieser Aktie sind aktuell nicht verfügbar.',
    why: 'Ohne diese Daten können wir nicht alles bewerten — der Score wird ungenauer.',
    for_trade: 'Bei vielen Lücken: Position kleiner halten oder ganz vermeiden, bis die Daten kommen.',
  },
  'watchlist-bewertung': {
    short: 'Watchlist-Bewertung',
    long: 'Eine schnelle Einschätzung pro Aktie auf deiner Beobachtungsliste.',
    meaning: 'Eine schnelle Einschätzung pro Aktie auf deiner Beobachtungsliste.',
    why: 'So siehst du auf einen Blick, welche Aktien gerade interessant sind und welche nicht.',
    for_trade: 'Hoher Score plus hohes Datenvertrauen = genauer ansehen. Niedrige Werte = erstmal nur beobachten.',
  },
  'ki-briefing': {
    short: 'KI-Briefing',
    long: 'Ein automatisch erstellter Tagesüberblick — die KI fasst zusammen, was heute wichtig war.',
    meaning: 'Ein automatisch erstellter Tagesüberblick — die KI fasst zusammen, was heute wichtig war.',
    why: 'Du musst nicht alles selbst durchgehen. Die KI sucht die wichtigen Veränderungen heraus.',
    for_trade: 'Erst die kritischen Punkte lesen. Dann Markt-Rückblick und Watchlist-Veränderungen.',
  },
  // K2: Erweiterungen für Klartext-Tooltips an den Hot Spots
  'breakout': {
    short: 'Ausbruch',
    long: 'Der Kurs durchbricht eine wichtige Widerstandsmarke nach oben.',
    meaning: 'Der Kurs ist über einen wichtigen Widerstand gestiegen — z. B. das letzte Hoch oder eine Trendlinie.',
    why: 'Ausbrüche zeigen, dass die Käufer übernommen haben. Ist der Ausbruch von Volumen begleitet, hält er meist.',
    for_trade: 'Kaufen direkt am Ausbruch (Breakout-Buy) oder beim ersten Pullback zur Ausbruchszone (sicherer).',
  },
  'pullback': {
    short: 'Pullback',
    long: 'Kurze Korrektur in einem laufenden Aufwärtstrend.',
    meaning: 'Der Kurs zieht sich kurz zurück, bevor er den Trend fortsetzt.',
    why: 'Pullbacks bieten bessere Einstiege als Käufe nahe am Hoch — du kaufst zur Stütze (z. B. MA20 / MA50).',
    for_trade: 'Idealer Einstieg ist, wenn der Kurs am MA50 oder MA20 dreht und mit grüner Kerze nach oben geht.',
  },
  'continuation': {
    short: 'Trend-Fortsetzung',
    long: 'Eine Aktie, die im Aufwärtstrend ist, macht eine kurze Pause und steigt dann weiter.',
    meaning: 'Der Aufwärtstrend läuft. Statt am höchsten Punkt zu kaufen, wartest du auf eine kurze Korrektur und steigst dann ein.',
    why: 'Sicherer als am Ausbruch zu kaufen — der Trend ist schon bewiesen, du bekommst einen besseren Preis.',
    for_trade: 'Warte bis der Kurs zur 50-Tage-Linie zurückkommt und dort dreht. Stop unter das letzte Pullback-Tief setzen.',
  },
  'stage-2-fortsetzung': {
    short: 'Stage-2-Fortsetzung (Pullback)',
    long: 'Du kaufst nicht am Ausbruch, sondern bei einer kurzen Korrektur in einem etablierten Aufwärtstrend.',
    meaning: 'Die Aktie ist schon seit Wochen im Aufwärtstrend (Stage 2 nach Stan Weinstein). Sie macht eine kurze Pause oder Korrektur. Genau dann steigst du ein.',
    why: 'Weniger Risiko als beim Ausbruch, weil der Trend schon bewiesen ist. Bessere Einstiegspreise.',
    for_trade: 'Warte auf einen Pullback zur 50-Tage-Linie oder Trendlinie. Erst kaufen, wenn dort eine grüne Kerze die Trendwende bestätigt.',
  },
  'stage-2-continuation': {
    short: 'Stage-2-Fortsetzung (Pullback)',
    long: 'Du kaufst nicht am Ausbruch, sondern bei einer kurzen Korrektur in einem etablierten Aufwärtstrend.',
    meaning: 'Die Aktie ist schon seit Wochen im Aufwärtstrend (Stage 2 nach Stan Weinstein). Sie macht eine kurze Pause oder Korrektur. Genau dann steigst du ein.',
    why: 'Weniger Risiko als beim Ausbruch, weil der Trend schon bewiesen ist. Bessere Einstiegspreise.',
    for_trade: 'Warte auf einen Pullback zur 50-Tage-Linie oder Trendlinie. Erst kaufen, wenn dort eine grüne Kerze die Trendwende bestätigt.',
  },
  'deep-value': {
    short: 'Deep-Value-Akkumulation',
    long: 'Eine günstige Qualitätsaktie wird in mehreren Tranchen gekauft, ohne Stop-Loss.',
    meaning: 'Du kaufst eine solide Aktie (gutes Geschäft, faire Bewertung), die gerade abgestürzt ist. Statt alles auf einmal zu kaufen, verteilst du den Kauf auf 3 bis 5 Stufen, jeweils bei tieferen Kursen.',
    why: 'Buffett- und Graham-Strategie. Wenn die Aktie noch tiefer fällt, kaufst du günstiger nach. Wenn sie steigt, hast du wenigstens einen Teil dabei. Kein Stop-Loss, weil Lehrbuch-Akkumulation Jahre läuft.',
    for_trade: 'Erste Tranche jetzt, weitere Tranchen alle 5 % weiter unten. Maximal 5 % vom Konto pro Tranche. Verkauf erst wenn Bewertung wieder hoch oder fundamentale Lage sich verändert.',
  },
  'donchian-breakout': {
    short: 'Donchian-Ausbruch',
    long: 'Du kaufst einen Coin, der ein neues 55-Tage-Hoch macht.',
    meaning: 'Der höchste Kurs der letzten 55 Tage wird durchbrochen — das ist ein klassisches Trend-Signal aus dem Trading-Lehrbuch der Turtle-Trader.',
    why: 'Wenn ein Coin ausbricht, läuft er oft monatelang weiter. Diese Strategie hat in den 1980ern Millionen verdient (Richard Dennis, Turtle Traders).',
    for_trade: 'Kaufen am Tagesschluss über dem 55-Tage-Hoch. Stop folgt 3× ATR unter dem höchsten Hoch seit Einstieg (Trail-Stop).',
  },
  'dual-momentum': {
    short: 'Dual-Momentum (ETF-Rotation)',
    long: 'Du hältst immer den ETF, der die letzten 12 Monate die beste Performance hatte — solange diese positiv war.',
    meaning: 'Eine einfache, robuste ETF-Strategie. Jeden Monat schaust du, welcher ETF die letzten 12 Monate am stärksten gestiegen ist. Den hältst du. Wenn keiner positiv ist, gehst du in Anleihen.',
    why: 'Strategie von Gary Antonacci. Hat über Jahrzehnte den Markt geschlagen mit weniger Risiko. Funktioniert weil Trends in Sektoren oft 6-18 Monate laufen.',
    for_trade: 'Einmal im Monat (am Monatsletzten) Performance vergleichen, ggf. umschichten. Sonst nichts tun.',
  },
  'wochen-ma40': {
    short: 'Wochen-MA40',
    long: '40-Wochen-Durchschnitt — entspricht ungefähr dem 200-Tage-Durchschnitt. Stan Weinstein nutzt ihn als Trendfilter.',
    meaning: 'Durchschnittskurs der letzten 40 Wochen (≈ 10 Monate).',
    why: 'Über dem MA40-Wochen sind Aktien laut Weinstein in „Stage 2" — also im Aufwärtstrend. Das ist die einzige Phase, in der Trend-Käufe nachhaltig funktionieren.',
    for_trade: 'Kurs über MA40-Wochen + steigender MA40-Wochen = grünes Licht für Long-Trades. Sonst lieber abwarten.',
  },
  'donchian': {
    short: 'Donchian-Range',
    long: 'Höchster Hoch und tiefster Tief der letzten N Tage. Klassischer Trend-Indikator.',
    meaning: 'Die Spanne zwischen dem höchsten Hoch und tiefsten Tief der letzten 55 Tage (oder einer anderen Länge).',
    why: 'Wenn der Kurs das Hoch durchbricht, ist das ein klares Trend-Signal — die Turtle-Trader haben damit Millionen verdient.',
    for_trade: 'Ausbruch über das Donchian-Hoch = Long-Setup. Stop unter das Donchian-Tief der letzten 20 Tage.',
  },
  'btc-dominance': {
    short: 'BTC-Dominanz',
    long: 'Anteil von Bitcoin am gesamten Krypto-Marktwert.',
    meaning: 'Wie viel Prozent vom Krypto-Gesamtmarkt steckt in Bitcoin (im Gegensatz zu Altcoins).',
    why: 'Hohe BTC-Dominanz (> 60 %) = Risk-off, Geld fließt aus Altcoins in Bitcoin. Niedrig (< 55 %) = Altcoin-Saison.',
    for_trade: 'Bei hoher Dominanz bleibst du eher bei Bitcoin oder gehst gar nicht long. Bei niedriger Dominanz ist Altcoin-Saison.',
  },
  // W36-QW5 (2026-05-17) — Konzepte aus Welle 32 (Thesis-Tracker), M24 W34d
  // (Score-Explainer Pillars/Risks), M12 (Percentile-Rank Quintile), M13
  // (Confidence-Decay). User-Feedback: "manche Felder ohne Erklärung".
  'pillars': {
    short: 'Stützen (Pillars)',
    long: 'Die Argumente, warum dieser Trade aufgehen sollte — möglichst konkret und messbar.',
    meaning: '3-5 Faktoren, die eintreten müssen damit die Investment-These trägt. Jeder Faktor hat eine konkrete Erwartung (z.B. "Cloud-Wachstum >25% YoY").',
    why: 'Ohne klare Stützen ist eine These nur Bauchgefühl. Mit klaren Stützen kannst Du wöchentlich prüfen ob Deine Annahmen halten.',
    for_trade: 'Beim Trade-Anlegen 3 echte Faktoren benennen — keine Floskeln wie "starkes Geschäftsmodell". Lieber "Free-Cashflow > $5 Mrd. in Q2".',
  },
  'risks-thesis': {
    short: 'Risiken (Falsifizierung)',
    long: 'Was würde Deine These klar widerlegen? Konkrete Schwellen statt vager Sorgen.',
    meaning: '3-5 Risiken mit jeweils einer messbaren Schwelle. Beispiel: "Op-Margin < 20%" oder "Wochenschluss unter MA50".',
    why: 'Nur was widerlegbar formuliert ist, kann ehrlich überprüft werden. Schwammige Risiken werden im Trade weggewünscht — konkrete Schwellen disziplinieren.',
    for_trade: 'Triggert eines der Risiken → Position überdenken, nicht "wegerklären". Sind Risiken im Plan klar, vermeidest Du FOMO und Bestätigungs-Bias.',
  },
  'conviction': {
    short: 'Conviction',
    long: 'Wie überzeugt bist Du gerade von der Investment-These? Skala 0-100 %.',
    meaning: 'Persönliche Überzeugung in Prozent. Sinkt automatisch wenn die wöchentliche KI-Prüfung neue Gegenargumente findet (z.B. -10 bei "weakens", -25 bei "invalidates").',
    why: 'Über Zeit sichtbar zu machen wie sich Deine eigene Überzeugung verändert hilft Pattern zu erkennen — z.B. "Ich verliere immer 4 Wochen vor dem Exit das Vertrauen".',
    for_trade: '>70% = volle Position halten. 50-70% = beobachten, Position-Größe ggf. reduzieren. <50% = Exit-Plan reaktivieren.',
  },
  'quintile': {
    short: 'Q1-Q5 (Rang)',
    long: 'Wo steht das Symbol im Vergleich zur Watchlist? Q1=Top-20%, Q5=Bottom-20%.',
    meaning: 'Q1 = besser als 80% der Watchlist (Top-Quintil mit Trophy-Icon). Q5 = schlechter als 80% (Bottom-Quintil). Nur sichtbar wenn ≥5 Vergleichswerte vorhanden.',
    why: 'Absoluter Score 78 sagt wenig — relativ zur eigenen Watchlist sagt es viel: Q1 = klares Fokus-Symbol, Q5 = vermutlich zum Aussortieren.',
    for_trade: 'Trades nur aus Q1/Q2 nehmen — die haben in Deiner Watchlist gerade die beste relative Score-Konstellation.',
  },
  'pre-trade-check': {
    short: 'Pre-Trade-Check',
    long: '6 Ampel-Checks die vor dem Speichern eines Plans automatisch laufen.',
    meaning: 'Earnings-Nähe, Plan-Score, CRV, Korrelation zu offenen Positions, Stop-Distanz, Sektor-Konzentration — alles in einer Ampel.',
    why: 'Verhindert die häufigsten Trade-Fehler: zu nah an Earnings, zu nah an offener Korrelations-Position, zu enger Stop ohne ATR-Bezug.',
    for_trade: 'Alle 6 grün = klares Setup. 1-2 gelb = bewusst entscheiden. 1+ rot = Plan überarbeiten BEVOR speichern.',
  },
  // W36-U3 (2026-05-17) — Makro + Sentiment-Indikatoren: User-Feedback
  // "ich sehe die Zahlen, weiß aber nicht was sie bedeuten".
  'aaii-bull-bear': {
    short: 'AAII Bull-Bear',
    long: 'Differenz zwischen Bull- und Bear-Anteil in der wöchentlichen AAII-Anlegerumfrage (Privatanleger).',
    meaning: 'Positiv (z.B. +2,5) = mehr Anleger erwarten steigende Kurse als fallende. Negativ = mehr Bären als Bullen.',
    why: 'Kontraindikator: extreme Stimmungen drehen oft. Über +30 = Markt zu euphorisch (oft kurz vor Korrektur). Unter -30 = Panik (oft Boden).',
    for_trade: 'Im normalen Bereich (-20 bis +20) ohne Signal. Bei Extremwerten lieber antizyklisch denken — nicht der Herde hinterherlaufen.',
  },
  'fear-greed': {
    short: 'Fear-&-Greed-Index',
    long: 'CNN-Sentiment-Index 0-100, kombiniert 7 Sub-Indikatoren zur Marktstimmung.',
    meaning: '0 = Extreme Fear (totale Panik), 50 = neutral, 100 = Extreme Greed (Euphorie). Aktualisiert sich täglich.',
    why: 'Markt-Profis nutzen den Index als Kontraindikator: bei Extreme Greed (>75) oft Topbildung, bei Extreme Fear (<25) oft Boden.',
    for_trade: '25-75 = normale Spanne, keine Sondersignale. >75 = vorsichtig werden (Gewinnmitnahmen erwägen). <25 = antizyklisch kaufen (Buffett-Style "Greedy when others are fearful").',
  },
  'pc-ratio-equity': {
    short: 'Put/Call-Ratio (Aktien)',
    long: 'Verhältnis von gehandelten Put-Optionen (= Wetten auf fallend) zu Call-Optionen (steigend) im US-Aktienmarkt.',
    meaning: '0,7 = mehr Calls als Puts (bullisch). 1,2 = mehr Puts als Calls (bärisch). Liegt typisch zwischen 0,5 und 1,5.',
    why: 'Auch Kontraindikator: <0,7 = Trader sehr optimistisch, oft Vorstufe zu Korrektur. >1,2 = Trader sehr ängstlich, oft kurz vor Boden.',
    for_trade: 'Im Bereich 0,7-1,2 keine Sondersignale. <0,7 → vorsichtig (zu viel Euphorie). >1,2 → antizyklisch (Panik-Verkauf könnte enden).',
  },
  'pc-ratio-index': {
    short: 'Put/Call-Ratio (Index)',
    long: 'Put/Call-Ratio für Index-Optionen (S&P 500 etc.) — institutionelles Hedging.',
    meaning: 'Höher = Profis sichern stärker ab. Niedriger = weniger Hedging-Aktivität.',
    why: 'Profis hedgen meist VOR Korrekturen. Steigender Index-P/C = institutionelle Vorsicht steigt.',
    for_trade: 'Plötzlicher Anstieg = Profis sehen Risiko. Selbst ohne Markt-Bewegung kann das ein Frühindikator sein.',
  },
  't10y2y': {
    short: 'Yield-Kurve (T10Y-T2Y)',
    long: 'Differenz zwischen 10-jährigen und 2-jährigen US-Staatsanleihen-Renditen.',
    meaning: 'Positiv (z.B. +0,5) = normale Kurve, lange Anleihen rentieren höher. Negativ ("invertiert") = lange < kurze Anleihen.',
    why: 'Invertierte Yield-Kurve gilt als zuverlässigster Rezessions-Vorbote (6-18 Monate Vorlauf). Wenn lange Anleihen weniger rentieren als kurze, erwartet der Markt Zinssenkungen → Rezession.',
    for_trade: '>0,5 = ungefährlich. 0-0,5 = nähert sich Invertierung. <0 = Rezessions-Warnung, Position-Sizing eher defensiv, längere Holds vermeiden.',
  },
  'hy-spread': {
    short: 'High-Yield-Spread',
    long: 'Renditeaufschlag von High-Yield-Bonds ("Junk-Bonds") gegenüber sicheren Staatsanleihen.',
    meaning: 'In Prozentpunkten. 2-3 % = entspannte Risiko-Stimmung, 5+ % = Stress am Kredit-Markt.',
    why: 'Bevor Aktien crashen, weiten sich die Junk-Bond-Spreads oft aus — Frühindikator für Liquiditäts-Stress.',
    for_trade: '<3 % = grünes Licht. 3-4 % = beobachten. >5 % = riskante Phase, neue Trades defensiv, Cash aufbauen.',
  },
  'fed-funds-rate': {
    short: 'Fed Funds Rate',
    long: 'Leitzins der US-Notenbank (Federal Reserve).',
    meaning: 'Der Zinssatz, zu dem Banken sich gegenseitig kurzfristig Geld leihen. Aktuell ~3,6 %.',
    why: 'Beeinflusst ALLE anderen Zinsen + Bewertung von Aktien (vor allem Wachstum/Tech). Steigende Zinsen → Druck auf KGV. Fallende → Rückenwind.',
    for_trade: 'Bei Fed-Sitzungen (FOMC) immer beobachten. Senkungs-Zyklus = Rückenwind für Aktien, vor allem Wachstum. Erhöhungs-Zyklus = Vorsicht bei hochbewerteten Aktien.',
  },
  'cpi-yoy': {
    short: 'CPI YoY',
    long: 'Verbraucherpreis-Inflation gegenüber dem Vorjahr (US-Inflation).',
    meaning: 'In Prozent. Fed-Ziel ist 2 %. Aktuell ~3,8 % = noch über Ziel.',
    why: 'Hohe Inflation → Fed bleibt restriktiv → Druck auf Aktien. Sinkende Inflation → Fed kann lockerer werden → Rückenwind.',
    for_trade: 'Bei CPI-Release immer im Briefing. Überraschung nach oben = Aktien-Verkaufsdruck. Nach unten = Rallye-Trigger.',
  },
  'unemployment': {
    short: 'Unemployment Rate',
    long: 'US-Arbeitslosenquote (BLS, monatlich am 1. Freitag).',
    meaning: 'Aktuell ~4,3 %. Vollbeschäftigung wird oft bei 3,5-4 % angenommen.',
    why: 'Steigt Arbeitslosigkeit → Rezessions-Sorge → Fed senkt Zinsen. Bei sehr niedriger Quote → Lohn-Inflation → Fed bleibt restriktiv.',
    for_trade: 'NFP-Release am 1. Freitag im Monat ist immer marktbewegend. Überraschungen in beide Richtungen können Volatilität auslösen.',
  },
  // W36-U4 — Catalyst-Event-Types (Tooltips bei Markt-Catalysts-Panel)
  'fomc': {
    short: 'FOMC',
    long: 'Federal Open Market Committee — Sitzung der US-Notenbank zur Zinsentscheidung.',
    meaning: '8x im Jahr. Entscheidet über Leitzins (Federal Funds Rate) + Dot-Plot (Zins-Pfad-Prognose der Fed-Mitglieder).',
    why: 'Größter Markt-Bewegungs-Treiber. Selbst kleine Überraschungen können S&P 1-2 % in Minuten bewegen. Trade-Plans sollten vor FOMC defensiver werden.',
    for_trade: 'Ein Tag vor FOMC: keine neuen Positionen. Bestehende Trades enger absichern (Stop nachziehen). Nach FOMC: erste Stunde abwarten, dann Reaktion bewerten.',
  },
  'cpi': {
    short: 'CPI',
    long: 'Consumer Price Index — US-Verbraucherpreis-Inflation (BLS, monatlich).',
    meaning: 'Wird als YoY (Year-over-Year) und MoM (Month-over-Month) berichtet. Headline = alles inkl. Energie, Core = ohne Energie/Lebensmittel.',
    why: 'Bestimmt Fed-Zinsentscheidung. Überraschung nach oben (heißer als erwartet) = Aktien-Druck (Fed muss restriktiv bleiben). Nach unten = Rallye-Trigger.',
    for_trade: 'CPI-Release immer im Briefing. Hochvolatile 30 min nach 14:30 Berlin-Zeit — keine knappen Stop-Loss in der Zeit.',
  },
  'pce': {
    short: 'PCE',
    long: 'Personal Consumption Expenditures — Fed-bevorzugter Inflations-Indikator (BEA).',
    meaning: 'Ähnlich CPI aber nutzt andere Berechnungsmethode (Verbrauchsverhalten passt sich Preisänderungen an). Fed schaut darauf bei Zinsentscheidungen.',
    why: 'Weniger volatil als CPI. Wenn PCE und CPI divergieren, hört die Fed eher auf PCE — wichtiger für Mittelfrist-Zinsausblick.',
    for_trade: 'Marktbewegung meist kleiner als CPI. Aber: Trend-Wechsel im PCE (z.B. erstes Mal seit 6 Monaten wieder steigend) ist starkes Signal.',
  },
  'nfp': {
    short: 'NFP',
    long: 'Non-Farm Payrolls — US-Arbeitsmarktbericht (1. Freitag im Monat, 14:30 Berlin).',
    meaning: 'Anzahl neu geschaffener Jobs außerhalb der Landwirtschaft. Konsens-Erwartung wird vorab veröffentlicht.',
    why: 'Sehr starker Markt-Mover. Zu starkes Wachstum → Fed-Sorge wegen Lohn-Inflation → Aktien-Druck. Schwache Zahlen → Rezessions-Sorge → ebenfalls Druck.',
    for_trade: '14:30 Berlin am 1. Freitag. Hohe Volatilität in den ersten 30 Min. Keine engen Stops in der Zeit, keine neuen Trades.',
  },
};

/**
 * K2: Mapping „Wort im Text" → Glossar-Schlüssel. Reihenfolge nach Spezifität:
 * längere Phrasen zuerst, damit "Wochen-MA40" nicht von "MA40" eingefangen wird.
 */
const TERM_PHRASES: Array<{ regex: RegExp; key: string }> = [
  // V9.TEXT: Strategie-Namen zuerst, weil sie die längsten Phrasen sind
  { regex: /\bDeep[\s-]?Value[\s-]?Accumulation\b/gi, key: 'deep-value' },
  { regex: /\bDeep[\s-]?Value\b/gi, key: 'deep-value' },
  { regex: /\bDonchian[\s-]?Breakout\b/gi, key: 'donchian-breakout' },
  { regex: /\bDual[\s-]?Momentum\b/gi, key: 'dual-momentum' },
  { regex: /\bStage[\s-]?2[\s-]?Continuation\b/gi, key: 'stage-2-continuation' },
  { regex: /\bStage[\s-]?2[\s-]?Pullback\b/gi, key: 'stage-2-fortsetzung' },
  { regex: /\bAvg[\s-]?R\b/gi, key: 'avg-r' },
  // Längere Phrasen zuerst (damit sie nicht von kürzeren überschrieben werden)
  { regex: /\bWochen-?MA40\b/gi, key: 'wochen-ma40' },
  { regex: /\bMA40-Wochen\b/gi, key: 'wochen-ma40' },
  { regex: /\bStage-2-Fortsetzung\b/gi, key: 'stage-2-fortsetzung' },
  { regex: /\bStage[ -]?2\b/gi, key: 'stage-2' },
  { regex: /\b(BTC|Bitcoin)-Dominanz\b/gi, key: 'btc-dominance' },
  { regex: /\bSektor-?Rotation\b/gi, key: 'sector-rotation' },
  { regex: /\bDatenvertrauen\b/gi, key: 'datenvertrauen' },
  { regex: /\bPlausibilit[äa]t\b/gi, key: 'plausibilitaet' },
  { regex: /\bEdge\b/g, key: 'edge' },
  { regex: /\bRelative St[äa]rke\b/gi, key: 'rs' },
  { regex: /\bAusschüttungsquote\b/gi, key: 'payout-ratio' },
  { regex: /\bPayout-?Ratio\b/gi, key: 'payout-ratio' },
  { regex: /\bDividenden-?Rendite\b/gi, key: 'dividend-yield' },
  { regex: /\bDividende[nd]?\b/gi, key: 'dividend-yield' },
  { regex: /\bFCF-?Marge\b/gi, key: 'fcf-marge' },
  { regex: /\bFree[\s-]?Cashflow\b/gi, key: 'fcf' },
  { regex: /\bFCF\b/g, key: 'fcf' },
  { regex: /\bOperative Marge\b/gi, key: 'operating-margin' },
  { regex: /\bOperating Margin\b/gi, key: 'operating-margin' },
  { regex: /\bNettomarge\b/gi, key: 'net-margin' },
  { regex: /\bNet Margin\b/gi, key: 'net-margin' },
  { regex: /\bVerschuldungsgrad\b/gi, key: 'debt-to-equity' },
  { regex: /\bDebt[\s/-]?to[\s/-]?Equity\b/gi, key: 'debt-to-equity' },
  { regex: /\bSharpe[\s-]?Ratio\b/gi, key: 'sharpe' },
  { regex: /\bVolatilit[äa]t\b/gi, key: 'volatility' },
  { regex: /\bDonchian-?55\b/gi, key: 'donchian-55' },
  { regex: /\bDonchian\b/gi, key: 'donchian' },
  { regex: /\bDatenvertrauen\b/gi, key: 'confidence' },
  { regex: /\bConfidence\b/gi, key: 'confidence' },
  { regex: /\bProfit[\s-]?Factor\b/gi, key: 'profit-factor' },
  { regex: /\bExpectancy\b/gi, key: 'expectancy' },
  { regex: /\bR-?Multiple\b/gi, key: 'r-multiple' },
  { regex: /\bFractional[\s-]?Shares\b/gi, key: 'fractional' },
  { regex: /\bSentiment\b/gi, key: 'sentiment' },
  { regex: /\bLiquidit[äa]t\b/gi, key: 'liquidity' },
  { regex: /\bVolumen-?Ratio\b/gi, key: 'volume-ratio' },
  { regex: /\bVolume-?Spike\b/gi, key: 'volume-ratio' },
  // Mittlere Länge
  { regex: /\bMA200\b/g, key: 'ma200' },
  { regex: /\bMA50\b/g, key: 'ma50' },
  { regex: /\bROIC\b/g, key: 'roic' },
  { regex: /\bROE\b/g, key: 'roe' },
  { regex: /\bROA\b/g, key: 'roa' },
  { regex: /\bATR\b/g, key: 'atr' },
  { regex: /\bCRV\b/g, key: 'crv' },
  { regex: /\bVCP\b/g, key: 'vcp' },
  { regex: /\bRisk-?Off\b/gi, key: 'risk-off' },
  { regex: /\bRisk-?On\b/gi, key: 'risk-on' },
  { regex: /\bDrawdown\b/gi, key: 'drawdown' },
  { regex: /\bPullback\b/gi, key: 'pullback' },
  { regex: /\bAusbruch\b/gi, key: 'breakout' },
  { regex: /\bBreakout\b/gi, key: 'breakout' },
  { regex: /\bContinuation\b/gi, key: 'continuation' },
  { regex: /\bEarnings\b/gi, key: 'earnings' },
  { regex: /\bPivot\b/gi, key: 'pivot' },
  { regex: /\bVolumen\b/gi, key: 'volumen' },
];

/**
 * Wandelt einen Klartext-String in JSX um, mit TermTooltips für bekannte Begriffe.
 * Wenn keine bekannten Begriffe drin sind, wird der String 1:1 zurückgegeben.
 */
export function enrichWithTooltips(text: string): React.ReactNode {
  if (!text) return text;
  // Finde alle Treffer mit Position, sortiere nach Position, baue dann Segmente
  const matches: Array<{ start: number; end: number; key: string; matched: string }> = [];
  for (const { regex, key } of TERM_PHRASES) {
    regex.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
      const start = m.index;
      const end = start + m[0].length;
      // Skippe wenn sich mit existierendem Match überlappt (längere Phrase gewinnt)
      if (matches.some((x) => start < x.end && end > x.start)) continue;
      matches.push({ start, end, key, matched: m[0] });
    }
  }
  if (matches.length === 0) return text;
  matches.sort((a, b) => a.start - b.start);
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  matches.forEach((m, i) => {
    if (cursor < m.start) parts.push(text.slice(cursor, m.start));
    parts.push(
      <TermTooltip key={`tt-${i}`} term={m.key}>
        {m.matched}
      </TermTooltip>
    );
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <>{parts}</>;
}

interface TermTooltipProps {
  term: keyof typeof TERM_GLOSSARY | string;
  children?: React.ReactNode;
  inline?: boolean;
}

export function TermTooltip({ term, children, inline }: TermTooltipProps) {
  const entry = TERM_GLOSSARY[term as keyof typeof TERM_GLOSSARY];
  const long = entry?.long ?? '';
  const label = children ?? entry?.short ?? term;
  const has3Blocks = entry?.meaning || entry?.why || entry?.for_trade;
  return (
    <span
      // W81 Mobile-Fix — tabIndex macht den Wrapper Touch-fokussierbar.
      // Touch-Tap → focus → focus-within → Tooltip via CSS sichtbar.
      // Tap ausserhalb → blur → Tooltip schliesst.
      tabIndex={0}
      className={cn(
        'group relative cursor-help focus:outline-none',
        inline ? 'inline-flex items-center gap-1' : 'inline-flex items-center gap-1'
      )}
      title={long}
    >
      <span className="border-b border-dashed border-current/40 [@media(hover:hover)]:group-hover:border-cockpit-accent group-focus-within:border-cockpit-accent">
        {label}
      </span>
      <Info className="size-3 opacity-50 [@media(hover:hover)]:group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:hover)]:group-hover:text-cockpit-accent group-focus-within:text-cockpit-accent" />
      {long && (
        <span
          role="tooltip"
          // W81 — group-focus-within zusaetzlich zu group-hover fuer Touch.
          className="pointer-events-none absolute left-0 top-full mt-1 z-50 hidden [@media(hover:hover)]:group-hover:block group-focus-within:block group-focus:block w-72 rounded-md border border-cockpit-border-strong bg-cockpit-surface3 px-3 py-2 text-[11px] leading-snug text-zinc-200 shadow-panel-hover space-y-1.5"
        >
          {has3Blocks ? (
            <>
              {entry?.meaning && (
                <span className="block">
                  <span className="text-[9px] uppercase tracking-wider text-cockpit-accent/80 font-mono">
                    Bedeutung
                  </span>
                  <span className="block opacity-95">{entry.meaning}</span>
                </span>
              )}
              {entry?.why && (
                <span className="block">
                  <span className="text-[9px] uppercase tracking-wider text-cockpit-accent/80 font-mono">
                    Warum wichtig
                  </span>
                  <span className="block opacity-95">{entry.why}</span>
                </span>
              )}
              {entry?.for_trade && (
                <span className="block">
                  <span className="text-[9px] uppercase tracking-wider text-cockpit-accent/80 font-mono">
                    Für deinen Trade
                  </span>
                  <span className="block opacity-95">{entry.for_trade}</span>
                </span>
              )}
            </>
          ) : (
            <span className="block">{long}</span>
          )}
        </span>
      )}
    </span>
  );
}
